import React, { useEffect, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";

// Friendly editor for the rich `protocols` collection. Renders structured fields
// so users don't have to hand-edit JSON.  Returns the full updated document via
// the `value` / `onChange` controlled API.

const TEXT_FIELDS = [
  { key: "title",              label: "Title",              type: "text",     required: true },
  { key: "protocol_id",        label: "Protocol ID",        type: "text",     required: true, help: "Stable identifier, e.g. proto_xyz" },
  { key: "id",                 label: "id (alias)",         type: "text",     help: "Usually identical to protocol_id" },
  { key: "category",           label: "Category",           type: "text" },
  { key: "guideline_source",   label: "Guideline source",   type: "text" },
];

const SHORT_TEXT = [
  { key: "definition",         label: "Definition",         rows: 3 },
  { key: "epidemiology",       label: "Epidemiology",       rows: 3 },
];

const SEVERITIES = ["mild", "moderate", "high", "critical"];
const STATUSES   = ["draft", "review", "published", "archived"];

// All list-of-strings fields. Rendered as a tag editor.
const LIST_FIELDS = [
  ["red_flags",                "Red flags"],
  ["presenting_complaints",    "Presenting complaints"],
  ["risk_factors",             "Risk factors"],
  ["clinical_features",        "Clinical features"],
  ["diagnostic_criteria",      "Diagnostic criteria"],
  ["investigations",           "Investigations"],
  ["initial_management",       "Initial management"],
  ["initial_stabilisation",    "Initial stabilisation"],
  ["monitoring",               "Monitoring"],
  ["complications",            "Complications"],
  ["referral_criteria",        "Referral criteria"],
  ["escalation_criteria",      "Escalation criteria"],
  ["admission_criteria",       "Admission criteria"],
  ["discharge_criteria",       "Discharge criteria"],
  ["follow_up_plan",           "Follow-up plan"],
  ["sa_resource_notes",        "SA resource notes"],
  ["clinical_pearls",          "Clinical pearls"],
  ["references",               "References"],
];

// Source attribution sub-object.
const SOURCE_FIELDS = [
  ["local_standard",         "Local standard (e.g. NDoH STG/EML)"],
  ["sa_society",             "SA specialty society guidance"],
  ["african_guidance",       "African / regional guidance"],
  ["international_evidence", "International evidence (AHA / ERC / WHO)"],
];

function StringListEditor({ value, onChange, placeholder, testid }) {
  const arr = Array.isArray(value) ? value : [];
  const update = (i, v) => {
    const next = arr.slice();
    next[i] = v;
    onChange(next);
  };
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const add = () => onChange([...arr, ""]);

  return (
    <div className="space-y-2" data-testid={testid}>
      {arr.map((item, i) => (
        <div key={i} className="flex gap-2 items-start">
          <Textarea
            value={item || ""}
            onChange={(e) => update(i, e.target.value)}
            rows={2}
            className="text-sm flex-1"
            placeholder={placeholder}
            data-testid={`${testid}-item-${i}`}
          />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() => remove(i)}
            className="text-rose-600 hover:bg-rose-50 mt-1"
            title="Remove"
            data-testid={`${testid}-remove-${i}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={add}
        className="text-[#111827] border-[#0B5FFF]/30"
        data-testid={`${testid}-add`}
      >
        <Plus className="w-3.5 h-3.5 mr-1" /> Add item
      </Button>
    </div>
  );
}

function AlgorithmStepsEditor({ value, onChange }) {
  const arr = Array.isArray(value) ? value : [];
  const update = (i, k, v) => {
    const next = arr.slice();
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const remove = (i) => {
    const next = arr.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, step: idx + 1 }));
    onChange(next);
  };
  const add = () => onChange([...arr, { step: arr.length + 1, title: "", description: "" }]);

  return (
    <div className="space-y-3" data-testid="protocol-form-algorithm">
      {arr.map((step, i) => (
        <div key={i} className="p-3 border border-slate-200 rounded-md bg-white">
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-full bg-[#0B5FFF] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
              {step.step ?? i + 1}
            </div>
            <div className="flex-1 space-y-2">
              <Input
                value={step.title || ""}
                onChange={(e) => update(i, "title", e.target.value)}
                placeholder="Step title (e.g. Confirm cardiac arrest)"
                className="text-sm font-medium"
                data-testid={`alg-step-title-${i}`}
              />
              <Textarea
                value={step.description || ""}
                onChange={(e) => update(i, "description", e.target.value)}
                placeholder="What the clinician should do…"
                rows={2}
                className="text-sm"
                data-testid={`alg-step-desc-${i}`}
              />
            </div>
            <Button type="button" size="icon" variant="ghost" onClick={() => remove(i)} className="text-rose-600 hover:bg-rose-50" data-testid={`alg-step-remove-${i}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={add} className="text-[#111827] border-[#0B5FFF]/30" data-testid="alg-step-add">
        <Plus className="w-3.5 h-3.5 mr-1" /> Add step
      </Button>
    </div>
  );
}

function DrugDosingEditor({ value, onChange }) {
  const obj = value && typeof value === "object" ? value : {};
  const groups = Object.keys(obj).length ? Object.keys(obj) : ["adult"];
  const ensure = (g) => Array.isArray(obj[g]) ? obj[g] : [];

  const setGroup = (g, arr) => onChange({ ...obj, [g]: arr });
  const renameGroup = (oldG, newG) => {
    if (!newG || newG === oldG) return;
    const next = { ...obj };
    next[newG] = next[oldG];
    delete next[oldG];
    onChange(next);
  };
  const removeGroup = (g) => {
    const next = { ...obj };
    delete next[g];
    onChange(next);
  };
  const addGroup = () => {
    const next = { ...obj };
    let i = 1;
    while (next[`group${i}`] !== undefined) i++;
    next[`group${i}`] = [];
    onChange(next);
  };

  const addRow = (g) => setGroup(g, [...ensure(g), { drug: "", dose: "", frequency: "" }]);
  const setRow = (g, i, k, v) => {
    const arr = ensure(g).slice();
    arr[i] = { ...arr[i], [k]: v };
    setGroup(g, arr);
  };
  const removeRow = (g, i) => setGroup(g, ensure(g).filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4" data-testid="protocol-form-drug-dosing">
      {groups.map((g) => (
        <div key={g} className="p-3 border border-slate-200 rounded-md bg-white">
          <div className="flex items-center justify-between gap-2 mb-2">
            <Input
              value={g}
              onChange={(e) => renameGroup(g, e.target.value)}
              className="text-xs h-7 w-40 font-mono"
              data-testid={`drug-group-name-${g}`}
            />
            <Button type="button" size="icon" variant="ghost" onClick={() => removeGroup(g)} className="text-rose-600 hover:bg-rose-50">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-2">
            {ensure(g).map((row, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-start">
                <Input value={row.drug || ""}      onChange={(e) => setRow(g, i, "drug", e.target.value)}      placeholder="Drug"      className="col-span-3 text-sm" data-testid={`drug-row-${g}-${i}-drug`} />
                <Input value={row.dose || ""}      onChange={(e) => setRow(g, i, "dose", e.target.value)}      placeholder="Dose"      className="col-span-4 text-sm" data-testid={`drug-row-${g}-${i}-dose`} />
                <Input value={row.frequency || ""} onChange={(e) => setRow(g, i, "frequency", e.target.value)} placeholder="Frequency" className="col-span-4 text-sm" data-testid={`drug-row-${g}-${i}-freq`} />
                <Button type="button" size="icon" variant="ghost" onClick={() => removeRow(g, i)} className="col-span-1 text-rose-600 hover:bg-rose-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          <Button type="button" size="sm" variant="outline" onClick={() => addRow(g)} className="mt-2 text-[#111827] border-[#0B5FFF]/30" data-testid={`drug-row-add-${g}`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add drug
          </Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={addGroup} className="text-slate-700" data-testid="drug-group-add">
        <Plus className="w-3.5 h-3.5 mr-1" /> Add group (e.g. paediatric, neonatal)
      </Button>
    </div>
  );
}

function SeverityClassEditor({ value, onChange }) {
  const arr = Array.isArray(value) ? value : [];
  const set = (i, k, v) => {
    const next = arr.slice();
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const add = () => onChange([...arr, { grade: "", criteria: "", management: "" }]);

  return (
    <div className="space-y-3" data-testid="protocol-form-severity-class">
      {arr.map((row, i) => (
        <div key={i} className="p-3 border border-slate-200 rounded-md bg-white space-y-2">
          <div className="flex items-center justify-between">
            <Input value={row.grade || ""} onChange={(e) => set(i, "grade", e.target.value)} placeholder="Grade label" className="text-sm w-48 font-medium" data-testid={`sev-grade-${i}`} />
            <Button type="button" size="icon" variant="ghost" onClick={() => remove(i)} className="text-rose-600 hover:bg-rose-50">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          <Textarea value={row.criteria || ""}  onChange={(e) => set(i, "criteria", e.target.value)}  placeholder="Criteria"  rows={2} className="text-sm" data-testid={`sev-criteria-${i}`} />
          <Textarea value={row.management || ""} onChange={(e) => set(i, "management", e.target.value)} placeholder="Management" rows={2} className="text-sm" data-testid={`sev-mgmt-${i}`} />
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={add} className="text-[#111827] border-[#0B5FFF]/30" data-testid="sev-add">
        <Plus className="w-3.5 h-3.5 mr-1" /> Add grade
      </Button>
    </div>
  );
}

function Section({ title, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-slate-50/40" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-slate-100"
      >
        <span className="font-heading font-semibold text-sm text-[#111827]">{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
      </button>
      {open && <div className="p-4 bg-white border-t border-slate-200">{children}</div>}
    </div>
  );
}

export default function ProtocolForm({ value, onChange }) {
  // value is the full protocol document object. We mutate it via spread so the
  // existing JSON shape is preserved end-to-end.
  const doc = value || {};
  const set = (k, v) => onChange({ ...doc, [k]: v });
  const setSource = (k, v) => onChange({ ...doc, source_attribution: { ...(doc.source_attribution || {}), [k]: v } });

  return (
    <div className="space-y-4" data-testid="protocol-form">
      {/* CORE — always open */}
      <Section title="Core" defaultOpen testid="proto-section-core">
        <div className="space-y-3">
          {TEXT_FIELDS.map((f) => (
            <div key={f.key}>
              <Label className="text-xs">{f.label}{f.required && " *"}</Label>
              <Input
                value={doc[f.key] ?? ""}
                onChange={(e) => set(f.key, e.target.value)}
                className="mt-1 text-sm"
                data-testid={`proto-${f.key}`}
              />
              {f.help && <div className="text-[10px] text-slate-500 mt-0.5">{f.help}</div>}
            </div>
          ))}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Severity</Label>
              <Select value={doc.severity || ""} onValueChange={(v) => set("severity", v)}>
                <SelectTrigger className="mt-1 text-sm" data-testid="proto-severity"><SelectValue placeholder="Pick severity" /></SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={doc.status || ""} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1 text-sm" data-testid="proto-status"><SelectValue placeholder="Pick status" /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={!!doc.is_emergency}
                onChange={(e) => set("is_emergency", e.target.checked)}
                id="proto-is-emergency"
                data-testid="proto-is-emergency"
              />
              <Label htmlFor="proto-is-emergency" className="text-xs">Emergency protocol (is_emergency)</Label>
            </div>
            <div>
              <Label className="text-xs">Emergency priority</Label>
              <Input
                type="number"
                value={doc.emergency_priority ?? ""}
                onChange={(e) => set("emergency_priority", e.target.value === "" ? undefined : parseInt(e.target.value))}
                className="mt-1 text-sm"
                data-testid="proto-emergency-priority"
              />
            </div>
          </div>

          {SHORT_TEXT.map((f) => (
            <div key={f.key}>
              <Label className="text-xs">{f.label}</Label>
              <Textarea
                value={doc[f.key] ?? ""}
                onChange={(e) => set(f.key, e.target.value)}
                rows={f.rows || 3}
                className="mt-1 text-sm"
                data-testid={`proto-${f.key}`}
              />
            </div>
          ))}
        </div>
      </Section>

      {/* ALGORITHM */}
      <Section title="Algorithm steps" defaultOpen testid="proto-section-algorithm">
        <AlgorithmStepsEditor
          value={doc.algorithm_steps}
          onChange={(v) => set("algorithm_steps", v)}
        />
      </Section>

      {/* DRUG DOSING */}
      <Section title="Drug dosing" testid="proto-section-drugs">
        <DrugDosingEditor
          value={doc.drug_dosing}
          onChange={(v) => set("drug_dosing", v)}
        />
      </Section>

      {/* SEVERITY CLASSIFICATION */}
      <Section title="Severity classification" testid="proto-section-sev-class">
        <SeverityClassEditor
          value={doc.severity_classification}
          onChange={(v) => set("severity_classification", v)}
        />
      </Section>

      {/* LIST FIELDS (group into a single accordion-style section) */}
      {LIST_FIELDS.map(([key, label]) => (
        <Section key={key} title={label} testid={`proto-section-${key}`}>
          <StringListEditor
            value={doc[key]}
            onChange={(v) => set(key, v)}
            placeholder={`Add a ${label.toLowerCase()} item…`}
            testid={`proto-list-${key}`}
          />
        </Section>
      ))}

      {/* SOURCE ATTRIBUTION */}
      <Section title="Source attribution" testid="proto-section-source">
        <div className="space-y-3">
          {SOURCE_FIELDS.map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs">{label}</Label>
              <Input
                value={doc.source_attribution?.[k] || ""}
                onChange={(e) => setSource(k, e.target.value)}
                className="mt-1 text-sm"
                data-testid={`proto-source-${k}`}
              />
            </div>
          ))}
        </div>
      </Section>

      <p className="text-[11px] text-slate-500 px-1">
        Any other fields present on this protocol are preserved untouched. Saving here locks the protocol from the periodic sync.
      </p>
    </div>
  );
}
